import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Link,
  Snackbar,
} from '@mui/material';
import {
  Receipt,
  ConfirmationNumber,
  Business as BusinessIcon,
  Person as PersonIcon,
  StickyNote2 as NotesIcon,
  Launch as LaunchIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { IconUserPlus } from '@tabler/icons-react';
import {
  SecondaryToolbar,
  ToolbarSearch,
  ToolbarRadio,
  ToolbarSpacer,
  ToolbarDivider,
  ToolbarButton,
} from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
  DataTablePagination,
} from '../components/table';
import type { CustomerIndexItemDto } from '@techgitter/contracts';
import {
  getCustomerIndexCached,
  getCustomerIndexCacheMetadata,
  invalidateCustomerIndexCache,
  refreshCustomerIndexCache,
  upsertCustomerIndexItem,
} from '../services/customerIndexCacheService';
import { DebugPanel } from '../components/debug/DebugPanel';
import { installApiCallLogger } from '../services/apiCallLogger';
import type { DebugPanelConfig } from '../types/debugPanel';
import { NewCustomerDialog } from '../components/customers/NewCustomerDialog';
import type { CustomerCreateResult } from '../services/customerCreateApi';
import { printLabel } from '../services/printApi';

installApiCallLogger();

const REPAIRSHOPR_BASE_URL = 'https://techsolvers.repairshopr.com';
const PAGE_SIZE = 100;

interface UserInfo {
  displayName: string;
  userPrincipalName: string;
  roles: string[];
}

interface CustomersPageProps {
  userInfo?: UserInfo | null;
}

export const CustomersPage: React.FC<CustomersPageProps> = ({ userInfo }) => {
  const navigate = useNavigate();
  const isAdmin = userInfo?.roles.includes('admin') ?? false;
  const [customers, setCustomers] = useState<CustomerIndexItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [designationFilter, setDesignationFilter] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [nameSortDir, setNameSortDir] = useState<'asc' | 'desc'>('asc');
  const [newCustomerOpen, setNewCustomerOpen] = useState(false);
  const [printMsg, setPrintMsg] = useState<
    { text: string; severity: 'success' | 'error' } | null
  >(null);

  // Clicking a customer (name or the new-customer flow) deep-links to the
  // /customer/:id route — the same page the customer-label QR opens — rather
  // than swapping in an inline view. We link by RepairShopr id (the route
  // resolves either that or the Dataverse GUID).
  const openCustomer = (id: string) => navigate(`/customer/${id}`);

  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getCustomerIndexCached();
      setCustomers(result.items);
      if (result.warning) {
        setError(result.warning);
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const designationOptions = useMemo(() => {
    const designations = new Set<string>();
    customers.forEach((c) => {
      if (c.designation) designations.add(c.designation);
    });
    return Array.from(designations).sort();
  }, [customers]);

  const filteredCustomers = useMemo(() => {
    return customers.filter((c) => {
      if (typeFilter && c.customerType !== typeFilter) return false;
      if (designationFilter && c.designation !== designationFilter) return false;

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        return (
          c.displayName?.toLowerCase().includes(query) ||
          c.email?.toLowerCase().includes(query) ||
          c.phone?.toLowerCase().includes(query) ||
          c.mobile?.toLowerCase().includes(query) ||
          c.domains?.toLowerCase().includes(query) ||
          c.alias?.toLowerCase().includes(query) ||
          c.designation?.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [customers, searchQuery, typeFilter, designationFilter]);

  const sortedCustomers = useMemo(() => {
    return [...filteredCustomers].sort((a, b) => {
      // Business always before Residential
      if (a.customerType !== b.customerType) {
        return a.customerType === 'Business' ? -1 : 1;
      }
      // Then sort by name
      const cmp = (a.displayName || '').localeCompare(b.displayName || '');
      return nameSortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredCustomers, nameSortDir]);

  const paginatedCustomers = useMemo(() => {
    const start = page * PAGE_SIZE;
    return sortedCustomers.slice(start, start + PAGE_SIZE);
  }, [sortedCustomers, page]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchQuery, typeFilter, designationFilter]);

  const handlePrintLabel = async (customer: CustomerIndexItemDto) => {
    try {
      const result = await printLabel({
        document: 'customer-label',
        data: {
          customerName: customer.businessName || customer.displayName || '',
          mobile: customer.mobile || customer.phone || '',
          email: customer.email || '',
          // QR opens the same /customer/:id page the customer name links to.
          qrUrl: `${window.location.origin}/customer/${customer.repairShoprCustomerId}`,
        },
        media: '90mm',
        copies: 1,
      });
      setPrintMsg(
        result.skipped
          ? {
              text: `Print skipped: ${result.reason ?? 'printer not configured'}`,
              severity: 'error',
            }
          : { text: `Label sent for ${customer.displayName}`, severity: 'success' },
      );
    } catch (e) {
      setPrintMsg({
        text: e instanceof Error ? e.message : 'Print failed',
        severity: 'error',
      });
    }
  };

  const openRsCustomer = (repairShoprCustomerId: string) => {
    window.open(
      `${REPAIRSHOPR_BASE_URL}/customers/${repairShoprCustomerId}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const openRsInvoices = (repairShoprCustomerId: string) => {
    window.open(
      `${REPAIRSHOPR_BASE_URL}/invoices?search%5Bcustomer_id%5D=${repairShoprCustomerId}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const openRsTickets = (repairShoprCustomerId: string) => {
    window.open(
      `${REPAIRSHOPR_BASE_URL}/tickets?commit=Search&ticket_search%5Bquery%5D%5Ball_fields%5D=customer_id%3A${repairShoprCustomerId}&ticket_search_id=all`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const formatAge = (cachedAt: number): string => {
    const diffMs = Date.now() - cachedAt;
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    return `${hours}h ${mins % 60}m`;
  };

  const debugConfig = useMemo<DebugPanelConfig>(() => ({
    sections: [
      { id: 'customers', label: 'Customers' },
    ],
    hookStates: [
      {
        name: 'loadCustomers',
        section: 'customers',
        status: error ? 'error' : loading ? 'loading' : 'loaded',
        error,
        recordCount: customers.length,
      },
    ],
    caches: [
      {
        name: 'Customer Index',
        section: 'customers',
        type: 'memory+localStorage',
        getRecordCount: () => getCustomerIndexCacheMetadata()?.recordCount ?? 0,
        getAge: () => {
          const meta = getCustomerIndexCacheMetadata();
          return meta ? formatAge(meta.cachedAt) : null;
        },
        getSizeEstimate: () => {
          try {
            const raw = localStorage.getItem('customer_index_cache_v2');
            if (!raw) return '0 KB';
            return `${Math.round(raw.length / 1024)} KB`;
          } catch { return 'unknown'; }
        },
        getData: () => customers,
        clear: () => { invalidateCustomerIndexCache(); loadCustomers(); },
        refresh: async () => { await refreshCustomerIndexCache(); await loadCustomers(); },
      },
    ],
    dataSections: [],
  }), [customers, error, loading, loadCustomers]);

  const handleCustomerCreated = async (result: CustomerCreateResult) => {
    setNewCustomerOpen(false);
    if (!result.customer) return;
    const c = result.customer;
    const displayName = c.businessName || c.fullName || `${c.firstName} ${c.lastName}`.trim();
    const customerType = c.businessName ? 'Business' : 'Residential';
    const indexItem: CustomerIndexItemDto = {
      dataverseCustomerId: result.dataverseCustomerId,
      repairShoprCustomerId: String(result.repairShoprCustomerId),
      displayName,
      customerType,
      fullName: c.fullName,
      businessName: c.businessName,
      designation: c.designation,
      email: c.email,
      phone: c.phone,
      mobile: c.mobile,
      domains: c.domains,
      alias: c.alias,
    };
    await loadCustomers();
    // Bridge Dataverse saved-query indexing lag: ensure the new record is
    // present in cache + state even if the immediate refetch missed it.
    upsertCustomerIndexItem(indexItem);
    setCustomers((prev) =>
      prev.some((p) => p.dataverseCustomerId === indexItem.dataverseCustomerId)
        ? prev
        : [...prev, indexItem],
    );
    // The upsert seeds the cache, so the /customer route resolves the new
    // record immediately even before the index refetch catches up.
    openCustomer(indexItem.repairShoprCustomerId);
  };

  return (
    <>
      {/* Contextual toolbar — floating card on the grey page surface
          (main-content--customers), matching the Invoices page. */}
      <SecondaryToolbar floating>
        <ToolbarSearch
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search customers…"
          maxWidth={300}
        />
        {/* Type filter — single-select (radio). null means no filter ("All"). */}
        <ToolbarRadio
          name="customer-type"
          label="All"
          active={typeFilter === null}
          onClick={() => setTypeFilter(null)}
        />
        <ToolbarRadio
          name="customer-type"
          label="Business"
          active={typeFilter === 'Business'}
          onClick={() => setTypeFilter('Business')}
        />
        <ToolbarRadio
          name="customer-type"
          label="Residential"
          active={typeFilter === 'Residential'}
          onClick={() => setTypeFilter('Residential')}
        />
        {designationOptions.length > 0 && (
          <>
            {/* Designation filter — single-select (radio). null means no filter. */}
            <ToolbarRadio
              name="customer-designation"
              label="All"
              active={designationFilter === null}
              onClick={() => setDesignationFilter(null)}
            />
            {designationOptions.map((d) => (
              <ToolbarRadio
                key={d}
                name="customer-designation"
                label={d}
                active={designationFilter === d}
                onClick={() => setDesignationFilter(d)}
              />
            ))}
          </>
        )}
        <ToolbarSpacer />
        {isAdmin && (
          <>
            <ToolbarDivider />
            <ToolbarButton
              variant="primary"
              icon={IconUserPlus}
              onClick={() => setNewCustomerOpen(true)}
            >
              New customer
            </ToolbarButton>
          </>
        )}
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px', maxWidth: 1600, mx: 'auto' }}>
      <NewCustomerDialog
        open={newCustomerOpen}
        onClose={() => setNewCustomerOpen(false)}
        onCreated={handleCustomerCreated}
      />

      {error && (
        <Alert severity="error" sx={{ marginBottom: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataTableCard>
          <DataTable>
            <thead>
              <tr>
                <DataTableHeadCell width={36} />
                <DataTableHeadCell
                  sortable
                  active
                  direction={nameSortDir}
                  onSort={() => setNameSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                >
                  Name
                </DataTableHeadCell>
                <DataTableHeadCell>Designation</DataTableHeadCell>
                <DataTableHeadCell>Alias</DataTableHeadCell>
                <DataTableHeadCell width={36} />
                <DataTableHeadCell align="center">RS Links</DataTableHeadCell>
              </tr>
            </thead>
            <tbody>
              {paginatedCustomers.length === 0 ? (
                <DataTableEmpty colSpan={6}>
                  {searchQuery || typeFilter || designationFilter
                    ? 'No customers match your filters'
                    : 'No customers found'}
                </DataTableEmpty>
              ) : (
                paginatedCustomers.map((customer) => (
                  <DataTableRow key={customer.dataverseCustomerId}>
                    <DataTableCell style={{ width: 36 }}>
                      <Tooltip title={customer.customerType}>
                        {customer.customerType === 'Business' ? (
                          <BusinessIcon fontSize="small" sx={{ color: 'primary.main', verticalAlign: 'middle' }} />
                        ) : (
                          <PersonIcon fontSize="small" sx={{ color: 'text.secondary', verticalAlign: 'middle' }} />
                        )}
                      </Tooltip>
                    </DataTableCell>
                    <DataTableCell style={{ maxWidth: 260 }}>
                      <Link
                        component="button"
                        type="button"
                        onClick={() => openCustomer(customer.repairShoprCustomerId)}
                        underline="hover"
                        sx={{
                          fontSize: '0.8rem',
                          maxWidth: 260,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                          textAlign: 'left',
                        }}
                      >
                        {customer.displayName}
                      </Link>
                    </DataTableCell>
                    <DataTableCell>{customer.designation || ''}</DataTableCell>
                    <DataTableCell muted style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {customer.alias || ''}
                    </DataTableCell>
                    <DataTableCell style={{ width: 36 }}>
                      {customer.hasNotes && (
                        <Tooltip title="Has notes">
                          <NotesIcon fontSize="small" sx={{ color: 'text.secondary', verticalAlign: 'middle' }} />
                        </Tooltip>
                      )}
                    </DataTableCell>
                    <DataTableCell align="center">
                      <Tooltip title="Print customer label">
                        <IconButton
                          size="small"
                          onClick={() => handlePrintLabel(customer)}
                        >
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Open in RepairShopr">
                        <IconButton
                          size="small"
                          onClick={() => openRsCustomer(customer.repairShoprCustomerId)}
                        >
                          <LaunchIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="RS Invoices">
                        <IconButton
                          size="small"
                          onClick={() => openRsInvoices(customer.repairShoprCustomerId)}
                        >
                          <Receipt fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="RS Tickets">
                        <IconButton
                          size="small"
                          onClick={() => openRsTickets(customer.repairShoprCustomerId)}
                        >
                          <ConfirmationNumber fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </DataTableCell>
                  </DataTableRow>
                ))
              )}
            </tbody>
          </DataTable>
          <DataTablePagination
            page={page}
            rowsPerPage={PAGE_SIZE}
            count={sortedCustomers.length}
            rowsPerPageOptions={[PAGE_SIZE]}
            onPageChange={setPage}
            onRowsPerPageChange={() => {}}
          />
        </DataTableCard>
      )}
      <DebugPanel config={debugConfig} />
      </Box>
      <Snackbar
        open={!!printMsg}
        autoHideDuration={4000}
        onClose={() => setPrintMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {printMsg ? (
          <Alert
            severity={printMsg.severity}
            onClose={() => setPrintMsg(null)}
          >
            {printMsg.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};
